import { RETENTION, SITE, SUB_PROCESSORS, TERMS_VERSION } from "@clinic/shared";
import type { LegalDoc, LegalDocId } from "./types";

const { legalName, cvr, address, email } = SITE.company;
/** Hvem "vi" er: den juridiske enhed, hvis den er udfyldt, ellers tjenestens navn. */
const operator = legalName || SITE.name;
const host = SITE.origin.replace(/^https?:\/\//, "");

const companyDetails = [cvr && `CVR-nr. ${cvr}`, address, email && `e-mail ${email}`].filter(Boolean).join("; ");

/** Hvordan man kontakter os, uden at opfinde en adresse der ikke findes. */
const contactUs = email ? `kontakt os på ${email}` : "kontakt os via oplysningerne ovenfor";
const contactUsElsewhere = email ? `kontakt os på ${email}` : "kontakt os via oplysningerne i privatlivspolitikken";

const whoWeAre: string[] = [
  `${SITE.name} (${host}) er en onlinebookingtjeneste for selvstændige klinikker${legalName ? `, som drives af ${legalName}` : ""}. I disse tekster betyder "vi" og "os" ${operator}.`,
  ...(companyDetails ? [`Virksomhedsoplysninger: ${companyDetails}.`] : []),
];

const purposeLabel: Record<(typeof SUB_PROCESSORS)[number]["purpose"], string> = {
  email: "Afsendelse af e-mails (bekræftelser, påmindelser, nulstilling af adgangskode, invitationer)",
  sms: "Afsendelse af sms-påmindelser, hvis klinikken slår dem til",
  hosting: "Hosting af applikationen, databasen og backups",
};

/** Oversætter de engelske landenavne i SUB_PROCESSORS; ukendte værdier vises uændret. */
const COUNTRIES: Record<string, string> = {
  Ireland: "Irland",
  Denmark: "Danmark",
  Germany: "Tyskland",
  Sweden: "Sverige",
  Netherlands: "Holland",
  France: "Frankrig",
  Belgium: "Belgien",
};
const daLocation = (location: string) => location.replace(/[A-Z][a-z]+/g, (w) => COUNTRIES[w] ?? w);
const daName = (name: string) =>
  name === "Hosting provider of the production server" ? "Hostingudbyder af produktionsserveren" : name;

const subProcessorTable = {
  head: ["Underdatabehandler", "Formål", "Placering"],
  rows: SUB_PROCESSORS.map((p) => [daName(p.name), purposeLabel[p.purpose], daLocation(p.location)]),
};

const backupNote =
  "Backups af databasen gemmes i op til 12 måneder (30 daglige og 12 månedlige kopier) og overskrives derefter. Oplysninger, der er slettet i det kørende system, kan derfor ligge i en backup, indtil den overskrives. Backups bruges kun til at genskabe tjenesten.";

const privacy: LegalDoc = {
  title: "Privatlivspolitik",
  description: `Sådan behandler ${SITE.name} personoplysninger om klinikker, deres kunder og medarbejdere: hvad vi gemmer, hvorfor, hvor længe og dine rettigheder.`,
  intro: `Senest opdateret ${TERMS_VERSION}. Her kan du læse, hvordan personoplysninger behandles, når du booker en tid via ${SITE.name}, når du arbejder på en klinik, der bruger tjenesten, og når du besøger ${host}.`,
  sections: [
    {
      heading: "Hvem vi er",
      paragraphs: whoWeAre,
    },
    {
      heading: "Vores rolle: klinikken bestemmer, vi behandler",
      paragraphs: [
        "Når du booker hos en klinik, er klinikken dataansvarlig for dine bookingoplysninger. Det er klinikken, der bestemmer, hvad oplysningerne bruges til, og vi behandler dem på klinikkens vegne som databehandler efter en databehandleraftale.",
        "For medarbejderkonti, administrationskonsollen og besøg på vores hjemmeside er vi selv dataansvarlige.",
        "Har du booket en tid og spørgsmål om dine oplysninger, skal du først og fremmest henvende dig til den klinik, du har booket hos.",
      ],
    },
    {
      heading: "Oplysninger om kunder, der booker",
      paragraphs: ["For hver klinik gemmer vi følgende om klinikkens kunder:"],
      list: [
        "Fulde navn og e-mailadresse",
        "Telefonnummer, hvis du oplyser det",
        "Dine bookinger: behandling, behandler, tidspunkt og status",
        "En valgfri besked til klinikken, hvis du skriver en",
        "Årsagen til en aflysning, hvis den oplyses",
        "Oplysninger om sms-påmindelser sendt til dig (telefonnummer og leveringsstatus), hvis klinikken bruger sms",
      ],
    },
    {
      heading: "Helbredsoplysninger",
      paragraphs: [
        "I bookingformularen bliver du bedt om ikke at skrive følsomme helbredsoplysninger i beskeden. Alligevel kan selve det, at du har booket en sundhedsrelateret behandling, fx fysioterapi eller kiropraktik, sige noget om dit helbred (persondataforordningens artikel 9).",
        "Klinikken er ansvarlig for at have et retsgrundlag for det, typisk at behandlingen er nødvendig af hensyn til sundhedsbehandling (artikel 9, stk. 2, litra h), eller et andet grundlag, der gælder for klinikken. Vi behandler kun oplysningerne efter klinikkens instruks og bruger dem ikke til andet.",
      ],
    },
    {
      heading: "Oplysninger om klinikkens medarbejdere og administratorer",
      list: [
        "Navn, e-mailadresse og rolle på klinikken",
        "Din adgangskode, som kun gemmes som en hashværdi",
        "Login-sessioner med IP-adresse og browsertype, som bruges til at beskytte din konto",
        "Links til nulstilling af adgangskode og invitationer, så længe de er gyldige",
        "For administrationskonsollen: administratorens e-mailadresse, login-sessioner med IP-adresse og en log over de handlinger, der udføres",
      ],
    },
    {
      heading: "Besøgende på hjemmesiden",
      paragraphs: [
        "Vi bruger ikke statistik, annoncering eller nogen form for sporing, og vi indlæser ingen scripts eller skrifttyper fra tredjeparter. Som på alle hjemmesider skal vores server bruge din IP-adresse for at kunne sende siden til dig. De få cookies, vi bruger, er beskrevet i cookiepolitikken.",
      ],
    },
    {
      heading: "Formål og retsgrundlag",
      table: {
        head: ["Formål", "Retsgrundlag"],
        rows: [
          [
            "Håndtering af bookinger, bekræftelser og påmindelser for en klinik",
            "Klinikkens eget retsgrundlag som dataansvarlig; vi handler efter klinikkens instruks (artikel 28)",
          ],
          [
            "Levering af medarbejderkonti og tjenesten til klinikker",
            "Opfyldelse af vores aftale med klinikken (artikel 6, stk. 1, litra b)",
          ],
          [
            "Sikring af konti og tjenesten, herunder sessions- og handlingslog",
            "Vores legitime interesse i at beskytte tjenesten og dens brugere (artikel 6, stk. 1, litra f)",
          ],
          [
            "Opbevaring af oplysninger efter bogføringsloven og anden lovgivning",
            "Retlig forpligtelse (artikel 6, stk. 1, litra c)",
          ],
        ],
      },
    },
    {
      heading: "E-mails, sms og kalenderfeeds",
      paragraphs: [
        "Vi sender bookingbekræftelser, besked når en booking ændres eller aflyses, påmindelser 24 timer før en aftale, e-mails til nulstilling af adgangskode og invitationer. E-mails sendes via Amazon SES i EU.",
        "Hvis en klinik slår sms-påmindelser til, sendes dit telefonnummer til vores sms-udbyder, GatewayAPI i Danmark, som leverer beskederne.",
        "En klinik kan lade sine behandlere abonnere på et privat kalenderfeed med deres aftaler. Feedet indeholder kundens navn og telefonnummer, men ikke beskeden. Det er klinikken, der beslutter, om funktionen bruges. Abonnerer en behandler, overføres oplysningerne til den kalendertjeneste, behandleren selv har valgt.",
      ],
    },
    {
      heading: "Underdatabehandlere og overførsler",
      paragraphs: [
        "Vi bruger følgende underdatabehandlere, som alle er bundet af en databehandleraftale.",
        "Alle data opbevares i EU/EØS, og vi overfører dem ikke til lande uden for EU/EØS. Skulle en underdatabehandler få behov for det, vil overførslen ske på grundlag af EU-Kommissionens standardkontraktbestemmelser eller et andet gyldigt overførselsgrundlag.",
      ],
      table: subProcessorTable,
    },
    {
      heading: "Hvor længe vi opbevarer oplysninger",
      table: {
        head: ["Oplysninger", "Opbevaring"],
        rows: [
          [
            "Kunder",
            `Anonymiseres ${RETENTION.customerMonths} måneder efter seneste booking, eller efter oprettelsen hvis kunden aldrig har booket`,
          ],
          [
            "Udløbne login-sessioner, nulstillingslinks og invitationer",
            `Slettes ${RETENTION.expiredSessionDays} dage efter udløb`,
          ],
          ["Oplysninger om sendte sms'er", `${RETENTION.smsMonths} måneder`],
          ["Log for administrationskonsollen", `${RETENTION.auditMonths} måneder`],
          [
            "En kliniks data efter abonnementets ophør",
            "Slettes senest 90 dage efter aftalens ophør, medmindre klinikken beder om sletning før",
          ],
        ],
      },
      paragraphs: [
        "En klinik kan desuden til enhver tid eksportere eller slette en enkelt kundes oplysninger fra sin bookingliste.",
        backupNote,
      ],
    },
    {
      heading: "Sådan beskytter vi oplysningerne",
      list: [
        "Al trafik er krypteret undervejs (HTTPS/TLS)",
        "Adgangskoder hashes med en moderne algoritme til adgangskoder og gemmes aldrig i læsbar form",
        "Hver kliniks data er adskilt fra andre klinikkers, og adskillelsen håndhæves i selve databasen (row-level security)",
        "Adgang til administrationskonsollen logges",
        "Der tages løbende backup",
      ],
    },
    {
      heading: "Dine rettigheder",
      paragraphs: ["Efter persondataforordningen (GDPR) har du ret til at:"],
      list: [
        "Få indsigt i de oplysninger, der er registreret om dig",
        "Få urigtige oplysninger berigtiget",
        "Få dine oplysninger slettet",
        "Få behandlingen af dine oplysninger begrænset",
        "Få udleveret dine oplysninger i et struktureret format (dataportabilitet)",
        "Gøre indsigelse mod behandling, der sker på grundlag af legitim interesse",
      ],
    },
    {
      heading: "Sådan bruger du dine rettigheder",
      paragraphs: [
        "Har du booket hos en klinik, skal du kontakte klinikken, da den er dataansvarlig for dine bookingoplysninger. Vi hjælper klinikkerne med at besvare den slags henvendelser, og klinikkerne har værktøjer til at eksportere og slette en kundes oplysninger.",
        `Gælder det medarbejderkonti, administrationskonsollen eller vores hjemmeside, så ${contactUs}.`,
      ],
    },
    {
      heading: "Klage",
      paragraphs: [
        "Du kan klage til Datatilsynet, www.datatilsynet.dk. Vi vil dog sætte pris på at få mulighed for at løse sagen med dig først.",
      ],
    },
    {
      heading: "Ændringer",
      paragraphs: [
        "Vi opdaterer politikken, når tjenesten eller lovgivningen ændrer sig. Datoen øverst viser, hvornår den sidst er opdateret.",
      ],
    },
  ],
};

const terms: LegalDoc = {
  title: "Vilkår og betingelser",
  description: `De vilkår, der gælder, når en klinik abonnerer på og bruger ${SITE.name}, onlinebookingtjenesten for selvstændige klinikker.`,
  intro: `Version ${TERMS_VERSION}. Disse vilkår gælder mellem ${operator} og hver klinik, der bruger ${SITE.name}. De accepteres sammen med databehandleraftalen, når klinikken oprettes.`,
  sections: [
    {
      heading: "Om os",
      paragraphs: whoWeAre,
    },
    {
      heading: "Hvem vilkårene gælder for",
      paragraphs: [
        `${SITE.name} er en erhvervstjeneste for klinikker og andre selvstændige behandlere. Når du opretter en klinik, bekræfter du, at du handler på vegne af en virksomhed og har ret til at acceptere vilkårene for den. Forbrugerbeskyttelsesreglerne gælder derfor ikke.`,
        "Klinikkens kunder booker via tjenesten, men er ikke part i disse vilkår. Deres aftale er med klinikken, og det er klinikken, der skal besvare kundernes spørgsmål om bookinger, behandlinger og betaling.",
      ],
    },
    {
      heading: "Tjenesten",
      paragraphs: [
        `Med ${SITE.name} kan en klinik vise sine behandlinger og åbningstider, tage imod onlinebookinger, sende bekræftelser og påmindelser til sine kunder og administrere medarbejdere og kalender. Vi kan løbende forbedre og ændre tjenesten, så længe de centrale funktioner fortsat er til rådighed.`,
      ],
    },
    {
      heading: "Konti og adgang",
      paragraphs: [
        "Klinikken er ansvarlig for, hvem den inviterer som medarbejdere, hvilke roller de får, og for alt, hvad der foretages via klinikkens konti. Hver person skal have sit eget login og holde sin adgangskode hemmelig.",
        `Har du mistanke om, at en konto er blevet misbrugt, skal du straks give os besked; ${contactUs}.`,
      ],
    },
    {
      heading: "Klinikkens ansvar",
      paragraphs: [
        "Klinikken har det fulde ansvar for de behandlinger, den tilbyder, sine priser, sine afbudsregler og sit forhold til sine kunder, herunder selve behandlingen og eventuel betaling mellem klinikken og kunden.",
        "Som dataansvarlig for kundernes oplysninger er klinikken ansvarlig for at have et retsgrundlag for behandlingen og for at oplyse sine kunder om den, som beskrevet i databehandleraftalen.",
      ],
    },
    {
      heading: "Acceptabel brug",
      paragraphs: ["Du må ikke bruge tjenesten til at:"],
      list: [
        "Overtræde lovgivningen eller krænke andres rettigheder",
        "Sende spam eller beskeder, som kunderne ikke har bedt om",
        "Uploade skadelig kode eller forsøge at få adgang til andre klinikkers data eller vores systemer",
        "Overbelaste, afsøge eller forstyrre tjenesten eller omgå dens sikkerhed eller begrænsninger",
        "Videresælge tjenesten eller stille den til rådighed for tredjemand uden vores skriftlige samtykke",
      ],
    },
    {
      heading: "Priser og betaling",
      paragraphs: [
        "Prisen er den plan, der blev aftalt ved tegning af abonnementet, eller som den fremgår af appen. Prisændringer varsles mindst 30 dage i forvejen, og du kan opsige aftalen, inden de træder i kraft.",
      ],
    },
    {
      heading: "Drift og support",
      paragraphs: [
        "Vi gør, hvad vi med rimelighed kan, for at holde tjenesten tilgængelig og velfungerende, men vi kan ikke garantere uafbrudt eller fejlfri drift. Planlagt vedligeholdelse sker så vidt muligt uden for klinikkernes almindelige åbningstid.",
      ],
    },
    {
      heading: "Data og databehandling",
      paragraphs: [
        "Klinikken ejer sine data. Vi bruger aldrig klinikkens eller kundernes oplysninger til egne formål, fx markedsføring, og vi sælger dem aldrig. Vores behandling af personoplysninger på klinikkens vegne er reguleret af databehandleraftalen, som er en del af disse vilkår. Vores egen behandling er beskrevet i privatlivspolitikken.",
        "Klinikken kan til enhver tid eksportere en enkelt kundes oplysninger. Når aftalen ophører, slettes klinikkens data senest 90 dage efter, medmindre klinikken beder om sletning før. Backups overskrives inden for 12 måneder.",
      ],
    },
    {
      heading: "Ansvar",
      paragraphs: [
        "Vores samlede erstatningsansvar efter disse vilkår er begrænset til det beløb, klinikken har betalt os i de 12 måneder før den begivenhed, der giver anledning til kravet.",
        "Vi hæfter ikke for indirekte tab, herunder driftstab, tabte bookinger eller tab af goodwill. Vi hæfter heller ikke for klinikkens behandlinger eller dens forhold til sine kunder.",
        "Begrænsningerne gælder ikke, hvor ansvaret ikke kan begrænses efter dansk ret, fx ved grov uagtsomhed eller forsæt.",
      ],
    },
    {
      heading: "Ændringer af vilkårene",
      paragraphs: [
        "Vi kan ændre disse vilkår. Væsentlige ændringer varsles til klinikken pr. e-mail mindst 30 dage, før de træder i kraft. Accepterer du ikke ændringerne, kan du opsige aftalen inden ikrafttrædelsen; fortsætter du med at bruge tjenesten, accepterer du dem.",
      ],
    },
    {
      heading: "Varighed og opsigelse",
      paragraphs: [
        "Aftalen løber, indtil den opsiges. Begge parter kan opsige den til udløbet af den igangværende abonnementsperiode. Vi kan suspendere eller lukke adgangen straks, hvis klinikken væsentligt misligholder vilkårene, fx ved misbrug af tjenesten eller manglende betaling.",
      ],
    },
    {
      heading: "Lovvalg og tvister",
      paragraphs: [
        "Vilkårene er underlagt dansk ret. Tvister, der ikke kan løses i mindelighed, afgøres ved de danske domstole med Københavns Byret som første instans.",
      ],
    },
    {
      heading: "Kontakt",
      paragraphs: [`Har du spørgsmål til vilkårene, så ${contactUs}.`],
    },
  ],
};

const dpa: LegalDoc = {
  title: "Databehandleraftale",
  description: `Databehandleraftalen (GDPR artikel 28) mellem ${SITE.name} og de klinikker, der bruger tjenesten til deres kunders bookinger.`,
  intro: `Version ${TERMS_VERSION}. Denne databehandleraftale fastlægger, hvordan ${operator} behandler personoplysninger på vegne af de klinikker, der bruger ${SITE.name}. Klinikken accepterer aftalen sammen med vilkårene, når klinikken oprettes.`,
  sections: [
    {
      heading: "Parter",
      paragraphs: [
        `Klinikken er dataansvarlig ("den dataansvarlige"). ${operator} er databehandler ("databehandleren"). Aftalen opfylder kravene i artikel 28 i persondataforordningen (GDPR).`,
        ...(companyDetails ? [`Oplysninger om databehandleren: ${companyDetails}.`] : []),
      ],
    },
    {
      heading: "Genstand og varighed",
      paragraphs: [
        `Databehandleren behandler personoplysninger for at levere ${SITE.name} til den dataansvarlige. Aftalen gælder, så længe databehandleren behandler personoplysninger på den dataansvarliges vegne, og ophører automatisk, når vilkårene ophører, og oplysningerne er slettet.`,
      ],
    },
    {
      heading: "Behandlingens karakter og formål",
      paragraphs: [
        "Opbevaring, strukturering, fremsøgning, videregivelse og sletning af oplysninger, så den dataansvarliges kunder kan booke tider online og få bekræftelser, ændringsbeskeder og påmindelser pr. e-mail og, hvis det er slået til, pr. sms, og så den dataansvarliges medarbejdere kan administrere bookinger og kalendere.",
      ],
    },
    {
      heading: "Kategorier af registrerede",
      list: [
        "Den dataansvarliges kunder (personer, der booker eller bliver booket)",
        "Den dataansvarliges medarbejdere og behandlere",
      ],
    },
    {
      heading: "Typer af personoplysninger",
      list: [
        "Kunder: navn, e-mailadresse, eventuelt telefonnummer, bookinger (behandling, behandler, tidspunkt, status), eventuel besked til klinikken, aflysningsårsag og oplysninger om sendte sms'er",
        "Medarbejdere: navn, e-mailadresse, rolle, hashet adgangskode, login-sessioner med IP-adresse og browsertype, nulstillingslinks og invitationer",
        "Særlige kategorier: en booking af en sundhedsrelateret behandling kan afsløre oplysninger om kundens helbred (artikel 9). Den dataansvarlige må ikke bede kunderne om at skrive yderligere helbredsoplysninger i fritekstfelter",
      ],
    },
    {
      heading: "Instruks",
      paragraphs: [
        "Databehandleren behandler kun personoplysninger efter dokumenteret instruks fra den dataansvarlige. Den dataansvarliges brug af tjenesten og de indstillinger, den vælger, udgør sammen med denne aftale instruksen. Behandling, der kræves efter EU-retten eller dansk ret, er undtaget; databehandleren underretter den dataansvarlige om et sådant krav, medmindre loven forbyder det.",
        "Databehandleren behandler ikke oplysningerne til egne formål. Databehandleren giver straks besked, hvis en instruks efter databehandlerens opfattelse er i strid med databeskyttelsesreglerne.",
      ],
    },
    {
      heading: "Fortrolighed",
      paragraphs: [
        "Kun personer, der har behov for adgang for at levere tjenesten, får adgang til den dataansvarliges oplysninger. De har tavshedspligt, enten efter aftale eller efter loven.",
      ],
    },
    {
      heading: "Sikkerhed",
      paragraphs: [
        "Databehandleren gennemfører passende tekniske og organisatoriske foranstaltninger efter artikel 32, herunder:",
      ],
      list: [
        "Kryptering af alle data under overførsel (HTTPS/TLS)",
        "Adgangskoder hashes med en moderne algoritme til adgangskoder",
        "Adskillelse af hver kliniks data, håndhævet i databasen (row-level security)",
        "Logning af adgang til administrationskonsollen",
        "Løbende backup, som gemmes i op til 12 måneder",
        "Automatisk sletning og anonymisering efter opbevaringsperioderne i privatlivspolitikken",
      ],
    },
    {
      heading: "Underdatabehandlere",
      paragraphs: [
        "Den dataansvarlige giver databehandleren en generel godkendelse til at bruge underdatabehandlere. De nuværende underdatabehandlere fremgår nedenfor.",
        "Databehandleren varsler den dataansvarlige pr. e-mail mindst 30 dage, før en underdatabehandler tilføjes eller udskiftes. Den dataansvarlige kan gøre indsigelse af saglige databeskyttelsesmæssige grunde. Kan parterne ikke blive enige, kan den dataansvarlige opsige tjenesten, inden ændringen træder i kraft.",
        "Databehandleren pålægger hver underdatabehandler de samme databeskyttelsesforpligtelser som i denne aftale og hæfter over for den dataansvarlige for underdatabehandlerens overholdelse af dem.",
      ],
      table: subProcessorTable,
    },
    {
      heading: "Overførsel til lande uden for EU/EØS",
      paragraphs: [
        "Personoplysningerne behandles inden for EU/EØS. Databehandleren overfører dem ikke til lande uden for EU/EØS, medmindre den dataansvarlige er varslet på forhånd, og overførslen sker på grundlag af EU-Kommissionens standardkontraktbestemmelser eller et andet gyldigt overførselsgrundlag efter forordningens kapitel V.",
      ],
    },
    {
      heading: "Bistand til den dataansvarlige",
      paragraphs: [
        "Under hensyn til behandlingens karakter bistår databehandleren den dataansvarlige med at besvare anmodninger fra de registrerede. Tjenesten har værktøjer, så den dataansvarlige selv kan eksportere eller slette en enkelt kundes oplysninger fra bookinglisten. Henvendelser, som databehandleren modtager direkte, sendes videre til den dataansvarlige.",
        "Databehandleren bistår også den dataansvarlige med at overholde artikel 32-36 (sikkerhed, anmeldelse af brud, konsekvensanalyser og forudgående høring) ud fra de oplysninger, databehandleren har adgang til.",
      ],
    },
    {
      heading: "Brud på persondatasikkerheden",
      paragraphs: [
        "Databehandleren underretter den dataansvarlige uden unødig forsinkelse og senest 48 timer efter at være blevet opmærksom på et brud på persondatasikkerheden. Underretningen beskriver, så vidt det vides, bruddets karakter, de berørte oplysninger og registrerede, de sandsynlige konsekvenser og de trufne foranstaltninger, så den dataansvarlige kan anmelde bruddet til Datatilsynet inden for 72 timer, når det kræves.",
      ],
    },
    {
      heading: "Sletning og tilbagelevering",
      paragraphs: [
        "Ved aftalens ophør kan den dataansvarlige eksportere sine data inden ophørsdatoen. Derefter sletter databehandleren den dataansvarliges personoplysninger senest 90 dage efter ophøret, medmindre den dataansvarlige beder om sletning før, eller lovgivningen kræver, at de opbevares.",
        backupNote,
      ],
    },
    {
      heading: "Revision og oplysninger",
      paragraphs: [
        "Databehandleren stiller efter anmodning de oplysninger til rådighed, der er nødvendige for at påvise overholdelse af denne aftale og artikel 28, og giver mulighed for og bidrager til revision og inspektion udført af den dataansvarlige eller en revisor, som den dataansvarlige har udpeget. Revision skal varsles med rimeligt varsel, og den dataansvarlige afholder sine egne omkostninger.",
      ],
    },
    {
      heading: "Den dataansvarliges forpligtelser",
      list: [
        "At sikre, at der er et retsgrundlag for behandlingen, også for eventuelle helbredsoplysninger (artikel 6 og 9)",
        "At give instrukser, der er i overensstemmelse med databeskyttelsesreglerne",
        "At oplyse sine kunder om behandlingen (artikel 13 og 14), fx i sin egen privatlivspolitik",
        "At beslutte, om sms-påmindelser og kalenderfeeds skal bruges, og på hvilket grundlag",
      ],
    },
    {
      heading: "Ansvar og forrang",
      paragraphs: [
        "Parternes ansvar efter denne aftale følger ansvarsbestemmelserne i vilkårene, i det omfang persondataforordningen tillader det. Er der modstrid mellem denne aftale og vilkårene om beskyttelse af personoplysninger, går denne aftale forud.",
      ],
    },
  ],
};

const cookies: LegalDoc = {
  title: "Cookiepolitik",
  description: `${SITE.name} bruger kun nødvendige cookies til login og husker dit sprogvalg. Ingen statistik, annoncering eller sporing fra tredjeparter.`,
  intro: `Senest opdateret ${TERMS_VERSION}. ${SITE.name} bruger så få cookies som muligt og ingen til sporing.`,
  sections: [
    {
      heading: "Det gemmer vi i din browser",
      table: {
        head: ["Navn", "Formål", "Varighed", "Type"],
        rows: [
          ["__Host-cb_session", "Holder klinikkens medarbejdere logget ind", "7 dage", "Nødvendig cookie (HttpOnly)"],
          [
            "__Host-cb_admin",
            "Holder administratorer logget ind i administrationskonsollen",
            "8 timer og højst 24 timer fra login",
            "Nødvendig cookie (HttpOnly)",
          ],
          ["cb_locale", "Husker det sprog, du har valgt", "Indtil du sletter det", "Funktionel (local storage)"],
        ],
      },
      paragraphs: [
        "Booker du blot en tid, sættes der ingen login-cookie. Login-cookierne sendes kun over krypterede forbindelser og kan ikke læses af scripts på siden. Sprogvalget gemmes i din browsers local storage og sendes aldrig til os.",
      ],
    },
    {
      heading: "Derfor beder vi ikke om samtykke",
      paragraphs: [
        "Cookierne er nødvendige for at levere den tjeneste, du har bedt om, fx at forblive logget ind, eller for at huske et valg, du selv har truffet. Efter ePrivacy-reglerne og cookiebekendtgørelsens § 4, stk. 2, kræver sådanne cookies ikke samtykke. Derfor viser vi ikke et cookiebanner.",
      ],
    },
    {
      heading: "Ingen sporing",
      paragraphs: [
        "Vi bruger ingen statistik, ingen annoncering, ingen tredjepartscookies og ingen form for sporing. Vi indlæser ikke skrifttyper eller scripts udefra, så ingen andre kan følge dig på vores side.",
      ],
    },
    {
      heading: "Sådan sletter du cookies",
      paragraphs: [
        "Du kan til enhver tid slette cookies og local storage i din browsers indstillinger. Sletter du login-cookien, bliver du logget ud; sletter du sprogvalget, vælger siden sprog igen.",
      ],
    },
    {
      heading: "Spørgsmål",
      paragraphs: [`Har du spørgsmål om cookies, så ${contactUsElsewhere}. Se også privatlivspolitikken.`],
    },
  ],
};

export const da: Record<LegalDocId, LegalDoc> = { privacy, terms, dpa, cookies };
